import React, { useState, useCallback, useRef } from "react";
import { Box } from "@mui/material";

/**
 * Interactive analogue clock with draggable hour and minute hands.
 * Inner half of clock: drag to set hour. Outer half: drag to set minute.
 * @param {number} hours - 1-12
 * @param {number} minutes - 0-59
 * @param {function} onChange - (hours, minutes) => void
 * @param {number} size - width/height in px
 */
const InteractiveAnalogClock = ({ hours = 12, minutes = 0, onChange, size = 220 }) => {
    const svgRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragHand, setDragHand] = useState(null); // 'hour' | 'minute'

    const r = size / 2 - 8;
    const cx = size / 2;
    const cy = size / 2;

    const angleToHour = useCallback((angleDeg) => {
        // 0 deg = 3 o'clock, 90 deg = 12 o'clock
        let h = (angleDeg + 90) / 30;
        if (h <= 0) h += 12;
        if (h > 12) h -= 12;
        return Math.round(h) || 12;
    }, []);

    const angleToMinute = useCallback((angleDeg) => {
        let m = Math.round((angleDeg + 90) / 6);
        if (m < 0) m += 60;
        if (m >= 60) m -= 60;
        return m;
    }, []);

    const getAngleFromEvent = useCallback((e) => {
        const el = svgRef.current;
        if (!el) return { angleDeg: 0, distRatio: 0.5 };
        const rect = el.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const x = clientX - rect.left - rect.width / 2;
        const y = clientY - rect.top - rect.height / 2;
        const angleRad = Math.atan2(y, x);
        const angleDeg = (angleRad * 180) / Math.PI;
        const dist = Math.sqrt(x * x + y * y);
        const distRatio = dist / (rect.width / 2);
        return { angleDeg, distRatio };
    }, []);

    const handlePointerDown = useCallback(
        (e) => {
            e.preventDefault();
            const { angleDeg, distRatio } = getAngleFromEvent(e);
            const hand = distRatio < 0.5 ? "hour" : "minute";
            setDragHand(hand);
            setIsDragging(true);
            if (hand === "hour") {
                const h = angleToHour(angleDeg);
                onChange(h, minutes);
            } else {
                const m = angleToMinute(angleDeg);
                onChange(hours, m);
            }
        },
        [hours, minutes, onChange, getAngleFromEvent, angleToHour, angleToMinute]
    );

    const handlePointerMove = useCallback(
        (e) => {
            if (!isDragging || !dragHand) return;
            e.preventDefault();
            const { angleDeg } = getAngleFromEvent(e);
            if (dragHand === "hour") {
                const h = angleToHour(angleDeg);
                onChange(h, minutes);
            } else {
                const m = angleToMinute(angleDeg);
                onChange(hours, m);
            }
        },
        [isDragging, dragHand, hours, minutes, onChange, getAngleFromEvent, angleToHour, angleToMinute]
    );

    const handlePointerUp = useCallback(() => {
        setIsDragging(false);
        setDragHand(null);
    }, []);

    React.useEffect(() => {
        if (!isDragging) return;
        const up = () => handlePointerUp();
        window.addEventListener("mouseup", up);
        window.addEventListener("touchend", up, { passive: true });
        return () => {
            window.removeEventListener("mouseup", up);
            window.removeEventListener("touchend", up);
        };
    }, [isDragging, handlePointerUp]);

    React.useEffect(() => {
        if (!isDragging) return;
        const move = (e) => handlePointerMove(e);
        window.addEventListener("mousemove", move);
        window.addEventListener("touchmove", move, { passive: false });
        return () => {
            window.removeEventListener("mousemove", move);
            window.removeEventListener("touchmove", move);
        };
    }, [isDragging, handlePointerMove]);

    // Hour hand: 30 deg per hour, plus 0.5 deg per minute
    const hourAngle = ((hours % 12) * 30 + minutes * 0.5 - 90) * (Math.PI / 180);
    const hourLength = r * 0.5;
    const hourX = cx + Math.cos(hourAngle) * hourLength;
    const hourY = cy + Math.sin(hourAngle) * hourLength;

    // Minute hand: 6 deg per minute
    const minuteAngle = (minutes * 6 - 90) * (Math.PI / 180);
    const minuteLength = r * 0.75;
    const minuteX = cx + Math.cos(minuteAngle) * minuteLength;
    const minuteY = cy + Math.sin(minuteAngle) * minuteLength;

    const clockNumbers = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

    return (
        <Box
            ref={svgRef}
            component="svg"
            viewBox={`0 0 ${size} ${size}`}
            width={size}
            height={size}
            sx={{
                display: "block",
                mx: "auto",
                cursor: isDragging ? "grabbing" : "grab",
                touchAction: "none",
                userSelect: "none",
            }}
            onMouseDown={handlePointerDown}
            onTouchStart={handlePointerDown}
            aria-label={`Interactive clock showing ${hours}:${String(minutes).padStart(2, "0")}`}
        >
            <circle cx={cx} cy={cy} r={r + 4} fill="none" stroke="#e53935" strokeWidth={6} />
            <circle cx={cx} cy={cy} r={r} fill="#fff" stroke="#bdbdbd" strokeWidth={2} />
            {Array.from({ length: 60 }).map((_, i) => {
                const angle = ((i * 6 - 90) * Math.PI) / 180;
                const isMajor = i % 5 === 0;
                const innerR = isMajor ? r - 12 : r - 6;
                const outerR = r;
                const x1 = cx + Math.cos(angle) * innerR;
                const y1 = cy + Math.sin(angle) * innerR;
                const x2 = cx + Math.cos(angle) * outerR;
                const y2 = cy + Math.sin(angle) * outerR;
                return (
                    <line
                        key={i}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={isMajor ? "#e53935" : "#e0e0e0"}
                        strokeWidth={isMajor ? 2.5 : 1}
                    />
                );
            })}
            {clockNumbers.map((num, i) => {
                const angle = ((i * 30 - 90) * Math.PI) / 180;
                const nr = r - 28;
                const x = cx + Math.cos(angle) * nr;
                const y = cy + Math.sin(angle) * nr;
                return (
                    <text
                        key={num}
                        x={x}
                        y={y}
                        textAnchor="middle"
                        dominantBaseline="central"
                        style={{
                            fontSize: 14,
                            fontWeight: 700,
                            fill: "#212121",
                            fontFamily: "system-ui, sans-serif",
                        }}
                    >
                        {num}
                    </text>
                );
            })}
            <line
                x1={cx}
                y1={cy}
                x2={hourX}
                y2={hourY}
                stroke="#1976d2"
                strokeWidth={5}
                strokeLinecap="round"
            />
            <line
                x1={cx}
                y1={cy}
                x2={minuteX}
                y2={minuteY}
                stroke="#e53935"
                strokeWidth={3}
                strokeLinecap="round"
            />
            <circle cx={cx} cy={cy} r={6} fill="#b2ebf2" stroke="#00838f" strokeWidth={1} />
        </Box>
    );
};

export default InteractiveAnalogClock;
